import { Button, EmptyState } from '@dev-spendesk/grapes';
import { useState, useMemo, useEffect } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { TravelPageWrapper } from 'modules/trips/components/TravelPageWrapper/TravelPageWrapper';
import {
  type TravelSearchState,
  isTravelSearchState,
} from 'modules/trips/models/TravelSearchState';
import { useModal } from 'src/core/common/hooks/useModalGrapes';
import { useParams } from 'src/core/common/hooks/useParams';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications/hooks/useNotifications';

import { Amenities } from './components/Amenities/Amenities';
import { BookingFooter } from './components/BookingFooter/BookingFooter';
import { PropertyImageGallery } from './components/PropertyImageGallery/PropertyImageGallery';
import { PropertyInfo } from './components/PropertyInfo/PropertyInfo';
import { RateSelection } from './components/RateSelection/RateSelection';
import { RoomSelection } from './components/RoomSelection/RoomSelection';
import { type PropertyDetails } from './types';
import {
  reshapePropertyDetails,
  usePropertyDetailsQuery,
} from '../../hooks/usePropertyDetailsQuery';
import { AddStayToTripModal } from './components/AddStayToTripModal/AddStayToTripModal';
import { CheckInOut } from './components/CheckInOut/CheckInOut';
import { PropertyDetailsPageSkeleton } from './components/PropertyDetailsPageSkeleton/PropertyDetailsPageSkeleton';
import { useBookProperty } from '../../hooks/useBookProperty';

export const PropertyDetailsPage = () => {
  const company = useCompany();
  const user = useUser();
  const { t } = useTranslation('global');
  const searchState = isTravelSearchState(history.location.state)
    ? (history.location.state as TravelSearchState)
    : undefined;
  const { id: propertyId } = useParams(routes.PROPERTY_DETAILS.path);
  const { data: propertyDetailsResponse, isLoading } =
    usePropertyDetailsQuery(propertyId);
  const { pushNotif } = useNotifications();
  const bookPropertyMutation = useBookProperty();

  const [selectedRoomIndex, setSelectedRoomIndex] = useState(0);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);

  const property: PropertyDetails | null = useMemo(() => {
    if (propertyDetailsResponse && propertyDetailsResponse.statusCode === 200) {
      return reshapePropertyDetails(propertyDetailsResponse.body.data.result);
    }
    return null;
  }, [propertyDetailsResponse]);

  const selectedRoom = property?.rooms[selectedRoomIndex] ?? null;
  const selectedRate = useMemo(() => {
    if (!selectedRoom || !selectedRateId) {
      return selectedRoom?.rateOptions[0] ?? null;
    }
    return (
      selectedRoom.rateOptions.find((rate) => rate.id === selectedRateId) ??
      null
    );
  }, [selectedRoom, selectedRateId]);

  const handleRoomSelect = (index: number) => {
    setSelectedRoomIndex(index);
    const newRoom = property?.rooms[index];
    const firstRateId = newRoom?.rateOptions[0]?.id ?? null;
    setSelectedRateId(firstRateId);
  };

  const nights = property
    ? calculateNights(
        property.bookingContext.checkInDate,
        property.bookingContext.checkOutDate,
      )
    : 0;

  useEffect(() => {
    if (property && property.rooms.length > 0) {
      const firstRoom = property.rooms[0];
      const firstRateId = firstRoom?.rateOptions[0]?.id ?? null;

      setSelectedRoomIndex(0);
      setSelectedRateId(firstRateId);
    }
  }, [property]);

  const [addStayToTripModal, showAddStayToTripModal] = useModal(
    ({ isOpen, onClose }) => {
      if (!property || !selectedRate?.totalPrice) {
        return <></>;
      }
      return (
        <AddStayToTripModal
          booking={{
            price: selectedRate.totalPrice,
            dates: {
              from: property.bookingContext.checkInDate,
              to: property.bookingContext.checkOutDate,
            },
          }}
          isOpen={isOpen}
          onClose={onClose}
          onBook={async (tripId: string) => {
            if (!property || !selectedRoom || !selectedRateId) {
              return;
            }

            try {
              await bookPropertyMutation.mutateAsync({
                propertyOfferId: property.propertyOfferId,
                tripId,
                bookerInformation: {
                  email: user.email,
                  name: user.fullname,
                  phone: `+${user.mobile_ext}${user.mobile_no}`,
                },
                room: selectedRoom,
                selectedRateId,
              });
              onClose();
              pushNotif({
                type: NotificationType.Success,
                message: t('travel.propertyDetails.successNotif'),
              });
              history.push(
                routeFor(routes.TRAVEL_TRIP_DETAILS.path, {
                  company: company.id,
                  id: tripId,
                }),
              );
            } catch {
              pushNotif({
                type: NotificationType.Danger,
                message: t('travel.propertyDetails.errorNotif'),
              });
            }
          }}
        />
      );
    },
  );

  if (isLoading) {
    return <PropertyDetailsPageSkeleton />;
  }

  if (!property) {
    return (
      <TravelPageWrapper
        onBack={() => {
          if (searchState) {
            history.push(
              routeFor(routes.TRAVEL_SEARCH.path, {
                company: company.id,
              }),
              searchState,
            );
          } else {
            history.push(
              routeFor(routes.TRAVEL_SEARCH.path, {
                company: company.id,
              }),
            );
          }
        }}
        title={t('travel.propertyDetails.header.title')}
      >
        <EmptyState
          title={t('travel.propertyDetails.emptyState.title')}
          subtitle={t('travel.propertyDetails.emptyState.description')}
          iconName="triangle-warning"
          iconVariant="warning"
          actions={
            <Button
              variant="secondaryNeutral"
              text={t('travel.propertyDetails.emptyState.cta')}
              onClick={() => {
                history.push(
                  routeFor(routes.TRAVEL_SEARCH.path, {
                    company: company.id,
                  }),
                );
              }}
            />
          }
        />
      </TravelPageWrapper>
    );
  }

  return (
    <>
      {addStayToTripModal}
      <TravelPageWrapper
        onBack={() => {
          if (searchState) {
            history.push(
              routeFor(routes.TRAVEL_SEARCH.path, {
                company: company.id,
              }),
              searchState,
            );
          } else {
            history.push(
              routeFor(routes.TRAVEL_SEARCH.path, {
                company: company.id,
              }),
            );
          }
        }}
        title={t('travel.propertyDetails.header.title')}
      >
        <div className="relative flex min-h-0 flex-1 px-40 pr-0 pb-0">
          <div className="w-[600px] min-w-0 shrink pt-24">
            <PropertyImageGallery
              photos={property.photos}
              propertyName={property.name}
            />
          </div>
          <div className="flex min-h-0 min-w-[500px] flex-1 flex-col gap-24 overflow-auto py-24 pr-24 pl-40">
            <PropertyInfo
              name={property.name}
              address={property.location.address.full}
              stars={property.rating.stars}
              reviewsCount={property.rating.reviewCount}
              description={property.description}
              coordinates={property.location.coordinates}
            />
            <CheckInOut
              checkIn={property.bookingContext.checkInDate}
              checkOut={property.bookingContext.checkOutDate}
            />
            <div className="separator" />
            <RoomSelection
              rooms={property.rooms}
              selectedRoomIndex={selectedRoomIndex}
              onSelectRoom={handleRoomSelect}
            />
            <div className="separator" />
            {selectedRoom && (
              <RateSelection
                rateOptions={selectedRoom.rateOptions}
                selectedRateId={selectedRateId}
                onSelectRate={setSelectedRateId}
              />
            )}
            <div className="separator" />
            <Amenities amenities={property.amenities} />
            <BookingFooter
              totalPrice={selectedRate?.totalPrice ?? null}
              nights={nights}
              travelers={property.bookingContext.adultsCount}
              onAddStayToTrip={() => {
                showAddStayToTripModal();
              }}
            />
          </div>
        </div>
      </TravelPageWrapper>
    </>
  );
};

const calculateNights = (checkIn: Date, checkOut: Date): number => {
  const diffTime = Math.abs(checkOut.getTime() - checkIn.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
